/**
 * The agent panel's Skills tab (B2), after DataViz on every agent page: the
 * customer's skills library, and a New skill menu. "Distil from this chat"
 * asks the agent to write the chat up as a draft skill (hoist_skill); the
 * draft opens in the editor when it reaches the catalog. "Write one from
 * scratch" opens the editor empty.
 *
 * Replaces the Skill Library drawer that Session options used to open.
 */
import React, { useState } from 'react'
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EditNoteIcon from '@mui/icons-material/EditNote'
import type { HoistedSkillInfo } from '../../agent'
import SkillsLibrary, { skillActionReasons } from './SkillsLibrary'
import SkillDetailDialog from './SkillDetailDialog'
import SkillDraftDialog from './SkillDraftDialog'
import useSkillsArea, { NO_CHAT_YET } from '../../hooks/useSkillsArea'
import useAccount from '../../hooks/useAccount'
import useWhoToAsk from '../../hooks/useWhoToAsk'

export interface AgentSkillsTabProps {
  customer: string
  /** Empty on the start page: there is no chat to distil or install into. */
  sessionId: string
  job: string
  jobs: string[]
  /** Skills the chat has installed (live skill_installed events). */
  installedSkillNames?: Set<string>
  hoistedSkill?: HoistedSkillInfo | null
  onSendMessage?: (message: string) => void
  /** The chat holds a finished agent turn; until then Distil is disabled. */
  hasCompletedTurn: boolean
}

export default function AgentSkillsTab({ customer, sessionId, job, jobs, installedSkillNames, hoistedSkill, onSendMessage, hasCompletedTurn }: AgentSkillsTabProps) {
  const account = useAccount()
  const whoToAsk = useWhoToAsk(customer)
  const currentUserEmail = account.user?.session_info?.email || ''
  const area = useSkillsArea({ customer, sessionId, job, jobs, hoisted: hoistedSkill, onSendMessage: sessionId ? onSendMessage : undefined, hasCompletedTurn })
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const viewingInstalled = area.viewing ? Boolean(area.viewing.installed) || Boolean(installedSkillNames?.has(area.viewing.name)) : false
  const viewingInstallReason = area.viewing
    ? skillActionReasons(area.viewing, { currentUserEmail, canPromote: area.canPromote, canManageBayesPrice: area.canManageBayesPrice, whoToAsk, installed: viewingInstalled }).install
    : undefined

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, '& > *': { flexShrink: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Company-level skills are used by the agent for everyone.
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="info"
          startIcon={<AddIcon />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ flexShrink: 0 }}
        >
          New skill
        </Button>
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{ disabledItemsFocusable: true }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          disabled={!area.canDistil}
          onClick={() => { setMenuAnchor(null); area.handleDistil() }}
          sx={!area.canDistil ? { alignItems: 'flex-start', '&.Mui-disabled': { opacity: 1 } } : undefined}
        >
          <ListItemIcon sx={!area.canDistil ? { color: 'text.disabled', mt: 0.5 } : undefined}><AutoAwesomeIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Distil from this chat"
            secondary={area.distilReason ?? 'The agent proposes skills from this chat; you pick which to draft'}
            primaryTypographyProps={!area.canDistil ? { color: 'text.disabled' } : undefined}
            secondaryTypographyProps={{ sx: { whiteSpace: 'normal', maxWidth: 260 } }}
          />
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); area.handleNew() }}>
          <ListItemIcon><EditNoteIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Write one from scratch" />
        </MenuItem>
      </Menu>

      <SkillsLibrary
        customer={customer}
        company={area.company}
        bayesprice={area.bayesprice}
        mine={area.mine}
        drafts={area.drafts}
        colleagues={area.colleagues}
        loading={area.isLoading}
        currentUserEmail={currentUserEmail}
        canPromote={area.canPromote}
        canManageBayesPrice={area.canManageBayesPrice}
        whoToAsk={whoToAsk}
        installedNames={installedSkillNames}
        onInstall={area.handleInstall}
        onView={area.handleView}
        onEdit={area.handleEdit}
        onCopy={area.handleCopySkill}
        onDelete={(s) => area.handleDelete(s.id)}
        onPromote={(s) => area.handlePromote(s.id)}
        onDemote={(s) => area.handleDemote(s.id)}
        onSaveDraft={(s) => area.handlePublish(s.id)}
        onDuplicate={area.handleDuplicateSkill}
        onMoveToBayesPrice={(s) => area.handleMoveToBayesPrice(s.id)}
        onSetOff={(s, off) => area.handleSetOff(s.id, off)}
        dense
      />

      <SkillDetailDialog
        skill={area.viewing}
        files={area.viewFiles}
        error={area.viewError}
        installed={viewingInstalled}
        installReason={viewingInstallReason}
        onInstall={area.handleInstall}
        onClose={area.handleCloseView}
      />
      {area.editing && (
        <SkillDraftDialog
          skill={area.editingSkill}
          files={area.editFiles}
          error={area.editError}
          saving={area.isSaving}
          testing={area.testing}
          testDisabledReason={!area.canTest
            ? 'Choose a job for this chat first: a test chat works on the same jobs.'
            : area.editingSkill
              ? skillActionReasons(area.editingSkill, { currentUserEmail, canPromote: area.canPromote, canManageBayesPrice: area.canManageBayesPrice, whoToAsk, installed: false }).test
              : undefined}
          onSaveDraft={area.handleSaveDraft}
          onPublish={area.handlePublishDraft}
          onSaveChanges={area.handleSaveChanges}
          bayesPriceNames={area.canManageBayesPrice ? area.bayesprice.map((b) => b.name) : undefined}
          onPublishToBayesPrice={area.canManageBayesPrice ? area.handlePublishDraftToBayesPrice : undefined}
          onDiscard={area.handleDiscard}
          onRunTest={area.handleRunTest}
          onOpenTest={area.handleOpenTest}
          onClose={area.handleCloseEdit}
        />
      )}
    </Box>
  )
}
